import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../stores/store";
import styles from "./CustomerDashboard.module.scss";
import { CustomerHomeIcon, LightsBacgroundIcon } from "../../../assets/icons";
import { useGetLoanRepaymentDataQuery, useGetWalletBalanceQuery } from "../../../service/charge-service";
import QuickServices from "./quick-services/QuickServices";
import RepaymentAlert from "./repayment-alert/RepaymentAlert";
import { useGetEnergyAccountQuery } from "../../../service/vending-service";
import { setEnergyProfileInfo, clearEnergyProfileInfo } from "../../../stores/slices/customer-energy-profile-slice";
import { MeterConsumptionSection } from "./meter-consumption/MeterConsumptionSection";
import { RecentTransactions } from "./recent-transactions/RecentTransactions";
import { SetUpWalletModal } from "./set-up-wallet-modal/SetUpWalletModal";
import { UnitRunningLowModal } from "./unit-running-low-modal/UnitRunningLowModal";
import { NotificationTrackline } from "./notification-trackline/NotificationTrackline";
import { NotificationType } from "../../../model/notifications/notifications";
import Can from "../../../guards/Can";
import { CustomerPermissions } from "../../../model/permissions/permissions";
import { useLazyGetMyBroadcastNotificationsQuery } from "../../../service/notification-service";
import { setBroadcastOnLogin, setBroadcastData } from "../../../stores/slices/broadcast-notification-slice";
import ScrollNotification from "./broadcast-notifications/ScrollNotification";
import PopupNotification from "./broadcast-notifications/PopupNotification";
import BannerNotification from "./broadcast-notifications/BannerNotification";
import MessageModal from "../../../common/ekedc-components/message-modal/MessageModal";
import bellGif from '../../../assets/gifs/notification-bell-icon.gif';


const CustomerDashboardPage = React.memo(() => {
	const dispatch = useDispatch();
	const [showAlert, setShowAlert] = useState(false);
	const [showWalletModal, setShowWalletModal] = useState(false);
	const walletModalShown = useRef(false);
	const [showUnitRunningLowModal, setShowUnitRunningLowModal] = useState(false);
	const [unitRunningLowUnits, setUnitRunningLowUnits] = useState<number | undefined>(undefined);
	const [showPopups, setShowPopups] = useState(true);
	const [showBanner, setShowBanner] = useState(true);
	const [showBox, setShowBox] = useState(true);
	const shownNotificationIds = useRef(new Set<string>());
	const notifications = useSelector((state: RootState) => state.notifications.notifications);
	const { data: loanRepaymentData } = useGetLoanRepaymentDataQuery(undefined, {
		selectFromResult: ({ data }) => ({ data }),
	});
	const { data: energyProfile, isError: energyProfileIsError } = useGetEnergyAccountQuery(undefined, {
		selectFromResult: ({ data, isError }) => ({ data, isError }),
	});
	const { data: walletBalance, isSuccess: walletLoaded } = useGetWalletBalanceQuery();

	const isOnLogin = useSelector((state: RootState) => state.broadcastNotification.isOnLogin);
	const fetchVersion = useSelector((state: RootState) => state.broadcastNotification.fetchVersion);
	const bannerNotification = useSelector((state: RootState) => state.broadcastNotification.data?.bannerNotification);
	const boxNotification = useSelector((state: RootState) => state.broadcastNotification.data?.boxNotification);
	const scrollNotification = useSelector((state: RootState) => state.broadcastNotification.data?.scrollNotification);
	const popupNotifications = useSelector((state: RootState) => state.broadcastNotification.data?.popupNotification);
	const [fetchBroadcastNotifications] = useLazyGetMyBroadcastNotificationsQuery();

	// Fetch on arrival to the dashboard (login or navigation)
	useEffect(() => {
		fetchBroadcastNotifications({ isOnLogin })
			.unwrap()
			.then(data => {
				dispatch(setBroadcastData(data));
				setShowPopups(true);
				setShowBanner(true);
				setShowBox(true);
			})
			.catch(error => console.error("Broadcast notifications error:", error));

		if (isOnLogin) dispatch(setBroadcastOnLogin(false));
	}, []);

	const lastFetchVersionRef = useRef(fetchVersion);
	useEffect(() => {
		if (fetchVersion === lastFetchVersionRef.current) return;
		lastFetchVersionRef.current = fetchVersion;
		console.log("[broadcast] refetching after socket...");
		fetchBroadcastNotifications({ isOnLogin: false })
			.unwrap()
			.then(data => {
				console.log("[broadcast] refetch data popupNotification:", data?.popupNotification);
				dispatch(setBroadcastData(data));
				setShowPopups(true);
				setShowBanner(true);
				setShowBox(true);
			})
			.catch(error => console.error("Broadcast notifications error:", error));
	}, [fetchVersion]);

	const lastSyncedProfileIdRef = useRef<string | null>(null);
	useEffect(() => {
		if (energyProfileIsError) {
			lastSyncedProfileIdRef.current = null;
			dispatch(clearEnergyProfileInfo());
			return;
		}
		if (energyProfile && energyProfile.id !== lastSyncedProfileIdRef.current) {
			lastSyncedProfileIdRef.current = energyProfile.id;
			dispatch(setEnergyProfileInfo({
				id: energyProfile.id,
				isDeleted: energyProfile.isDeleted,
				userId: energyProfile.userId,
				meterNumber: energyProfile.meterNumber ?? energyProfile.accountNumber,
				accountNumber: energyProfile.accountNumber,
				meterAddress: energyProfile.meterAddress,
				utilityId: energyProfile.utilityId,
				utility: energyProfile.utility ?? null,
				consumerType: energyProfile.consumerType,
				pendingStatus: energyProfile.pendingStatus ?? null,
				preferRemoteTokenLoad: energyProfile.preferRemoteTokenLoad ?? null,
				isNinVerified: energyProfile.isNinVerified ?? false,
				isNinPhoneNumberVerified: energyProfile.isNinPhoneNumberVerified ?? false,
				residencyStatus: energyProfile.residencyStatus ?? null,
				nin: energyProfile.nin ?? null,
				createdAt: energyProfile.createdAt,
				updatedAt: energyProfile.updatedAt,
			}));
		}
	}, [energyProfile, energyProfileIsError, dispatch]);

	useEffect(() => {
		if (walletModalShown.current) return;
		if (!walletLoaded) return;
		const hasWallet = walletBalance?.walletDetails && walletBalance.walletDetails.length > 0;
		if (hasWallet) return;

		const timer = setTimeout(() => {
			if (!walletModalShown.current) {
				walletModalShown.current = true;
				setShowWalletModal(true);
			}
		}, 3000);

		return () => clearTimeout(timer);
	}, [walletLoaded, walletBalance]);

	useEffect(() => {
		const latest = notifications[0];
		if (!latest || shownNotificationIds.current.has(latest.id)) return;
		if (
			latest.type === String(NotificationType.UnitRunningLow) ||
			latest.type === 'UnitRunningLow'
		) {
			shownNotificationIds.current.add(latest.id);
			const units = latest.data?.unitsRemaining;
			setUnitRunningLowUnits(units != null ? parseFloat(units) : undefined);
			setShowUnitRunningLowModal(true);
		}
	}, [notifications]);

	const handlePayNow = () => {};

	return (
		<div className={styles.dashboard}>
			{showBanner && bannerNotification && (
				<BannerNotification notification={bannerNotification} onClose={() => setShowBanner(false)} />
			)}
			{showBox && boxNotification && boxNotification.turnOnOff !== false && boxNotification.message && (
				<MessageModal
				icon={<img src={bellGif} alt="" width={96} height={96} />}
					title={boxNotification.title}
					message={boxNotification.message}
					onClose={() => setShowBox(false)}
				/>
			)}
			{showPopups && !!popupNotifications?.length && (
				<PopupNotification notifications={popupNotifications} onClose={() => setShowPopups(false)} />
			)}
			{showWalletModal && <SetUpWalletModal onClose={() => setShowWalletModal(false)} />}
			{showUnitRunningLowModal && (
				<UnitRunningLowModal
					unitsRemaining={unitRunningLowUnits}
					onClose={() => setShowUnitRunningLowModal(false)}
				/>
			)}

			{!!loanRepaymentData && showAlert && (
				<RepaymentAlert
					amount={loanRepaymentData?.billPaymentProcesses[0]?.totalAmount.amount ?? 0}
					dueDate={loanRepaymentData?.billPaymentProcesses[0]?.startDate ?? ""}
					currencySymbol={loanRepaymentData?.billPaymentProcesses[0]?.totalAmount.currency}
					onPayClick={handlePayNow}
					onClose={() => setShowAlert(false)}
					className={styles.repaymentAlert}
				/>
			)}
			<div className={styles.mainContent}>
				<div className={styles.lightsBackground}>
					<LightsBacgroundIcon />
				</div>
				<div className={styles.content}>
					<div className={styles.rightSection}>
						<MeterConsumptionSection />
						{scrollNotification && <ScrollNotification notification={scrollNotification} />}
						<NotificationTrackline loanRepaymentData={loanRepaymentData} />
						<QuickServices className={styles.quickServices} />
					</div>
					<Can permission={CustomerPermissions.ViewTransactionHistory}>
						<RecentTransactions />
					</Can>
				</div>
			</div>
		</div>
	);
});

export default CustomerDashboardPage;
